import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listUsers, login } from "@/lib/api";
import { useApp } from "@/lib/AppContext";
import { Lock, ScanBarcode } from "lucide-react";
import { TID } from "@/constants/testIds";

const ROLE_LABEL = {
  cajero: "Cajero/a",
  encargado: "Encargado/a",
  supervisor: "Supervisor",
};

export default function Login() {
  const navigate = useNavigate();
  const { setUser, setToken } = useApp();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    listUsers().then(setUsers).catch(() => setErr("No se ha podido cargar la lista de usuarios"));
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!selected) {
      setErr("Selecciona un usuario");
      return;
    }
    setErr("");
    setLoading(true);
    try {
      const r = await login(selected.username, pin);
      setUser(r.user);
      setToken(r.token);
      navigate("/pos");
    } catch (e) {
      setErr("PIN incorrecto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="h-screen flex"
      style={{
        backgroundImage:
          "linear-gradient(105deg, rgba(15,23,42,0.78) 0%, rgba(15,23,42,0.45) 60%, rgba(15,23,42,0) 100%), url(https://images.unsplash.com/photo-1511317559916-56d5ddb62563?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Branding side */}
      <div className="flex-1 hidden md:flex flex-col justify-between p-12 text-white">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-white text-slate-900 flex items-center justify-center font-display font-bold">
            T
          </div>
          <div className="font-display text-2xl font-semibold">TPV Retail</div>
        </div>
        <div className="max-w-xl">
          <div className="text-xs uppercase tracking-[0.3em] text-white/70 mb-3">Verifactu Ready · Sage 200c</div>
          <h1 className="font-display text-5xl font-semibold leading-tight">
            Cobra rápido.<br />Cumple la Ley Antifraude.
          </h1>
          <p className="mt-4 text-white/80 max-w-md">
            TPV de retail con motor Verifactu integrado, encadenamiento de huellas SHA-256, QR de cotejo AEAT
            y conexión en tiempo real con Sage 200c.
          </p>
        </div>
        <div className="text-xs text-white/60">© {new Date().getFullYear()} · Demo MVP · RD 1007/2023 · Orden HAC/1177/2024</div>
      </div>

      {/* Login panel */}
      <div className="w-full md:w-[460px] bg-white p-8 md:p-10 flex flex-col overflow-y-auto thin-scroll">
        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-[0.25em]">
          <Lock size={14} /> Acceso operadores
        </div>
        <h2 className="font-display text-3xl font-semibold mt-2 text-slate-900">Inicia turno</h2>
        <p className="text-sm text-slate-500 mt-1">Selecciona tu usuario e introduce el PIN.</p>

        <div className="grid grid-cols-2 gap-2 mt-6">
          {users.map((u) => (
            <button
              key={u.id}
              data-testid={TID.loginUserCard(u.id)}
              onClick={() => setSelected(u)}
              className={`text-left p-3 rounded-md border transition-colors ${
                selected?.id === u.id
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 hover:border-slate-400 bg-white"
              }`}
            >
              <div className="font-medium text-sm">{u.name}</div>
              <div className={`text-[11px] uppercase tracking-wider mt-1 ${selected?.id === u.id ? "text-white/70" : "text-slate-500"}`}>
                {ROLE_LABEL[u.role]} · @{u.username}
              </div>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <input
            data-testid={TID.loginUser}
            type="text"
            value={selected?.username || ""}
            readOnly
            placeholder="Selecciona un usuario"
            className="w-full h-12 px-4 rounded-md border border-slate-200 bg-slate-50 text-sm font-mono"
          />
          <input
            data-testid={TID.loginPin}
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="PIN (1234 / 2222 / 1111)"
            className="w-full h-12 px-4 rounded-md border border-slate-200 text-lg font-mono tracking-[0.4em]"
          />
          {err && <div className="text-sm text-red-600">{err}</div>}
          <button
            data-testid={TID.loginSubmit}
            disabled={loading}
            type="submit"
            className="w-full h-14 rounded-md bg-slate-900 text-white font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <ScanBarcode size={18} /> {loading ? "Verificando…" : "Acceder"}
          </button>
        </form>

        <div className="mt-auto pt-6 text-[11px] text-slate-400">
          Demo: admin / 1234 · encargado / 2222 · cajero1 · cajero2 / 1111
        </div>
      </div>
    </div>
  );
}
